import { useCallback } from 'react'

import { useUiActionRunner } from './useUiActionRunner'

export type ApprovableToolCall = { id?: string; toolCallMessageId?: string }

export type ToolCallApprovalFallback<TMessage extends ApprovableToolCall> = {
  approve: (message: TMessage) => Promise<void>
  reject: (message: TMessage, reason?: string) => Promise<void>
}

export type ToolCallApproval<TMessage extends ApprovableToolCall> = {
  approve: (message: TMessage) => Promise<void>
  reject: (message: TMessage, reason?: string) => Promise<void>
}

/**
 * Decides what a user's Approve or Reject means for one tool call.
 *
 * The browser starts work on the toolCall event and the approval card appears on the
 * toolCallPendingApproval event right after, so both buttons are live for as long as the operation
 * runs — while a confirm dialog waits for an answer, or a form is still filling. Answering then
 * would settle a tool call whose work has not happened.
 */
export function useToolCallApproval<TMessage extends ApprovableToolCall>(
  fallback: ToolCallApprovalFallback<TMessage>,
  onError: (message: string) => void
): ToolCallApproval<TMessage> {
  const { commit, abandon, awaitOperationFor, isLinkedTo } = useUiActionRunner()

  const resolve = useCallback(
    async (message: TMessage, linked: () => Promise<{ status: string; message?: string }>, bare: () => Promise<void>) => {
      const sourceId = message.toolCallMessageId ?? message.id
      const waited = sourceId ? await awaitOperationFor(sourceId) : false

      // Read through isLinkedTo rather than the linkedSource state: the await above outlives this
      // closure's copy of it.
      if (sourceId && isLinkedTo(sourceId)) {
        const outcome = await linked()
        if (outcome.status === 'error' && outcome.message) onError(outcome.message)
        return
      }

      // Unlinked after waiting means the runner settled the call itself while we waited.
      if (waited) return

      await bare()
    },
    [awaitOperationFor, isLinkedTo, onError]
  )

  const approve = useCallback((message: TMessage) => resolve(message, commit, () => fallback.approve(message)), [commit, fallback, resolve])

  const reject = useCallback(
    (message: TMessage, reason?: string) =>
      resolve(
        message,
        () => abandon(reason),
        () => fallback.reject(message, reason)
      ),
    [abandon, fallback, resolve]
  )

  return { approve, reject }
}
